/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

// @ts-expect-error TS2307
import BpmnModdle from 'bpmn-moddle';
import BpmnModelerModdleExtension from 'modeler-moddle/resources/modeler.json';
import BpmnZeebeModdleExtension from 'zeebe-bpmn-moddle/resources/zeebe.json';
import DmnModdle from 'dmn-moddle';
import DmnModelerModdleExtension from 'modeler-moddle/resources/dmn-modeler.json';
import DmnZeebeModdleExtension from 'zeebe-dmn-moddle/resources/zeebe.json';

export function createBpmnModdle() {
  return new BpmnModdle({
    modeler: BpmnModelerModdleExtension,
    zeebe: BpmnZeebeModdleExtension
  });
}

export function createDmnModdle() {
  return new DmnModdle({
    modeler: DmnModelerModdleExtension,
    zeebe: DmnZeebeModdleExtension
  });
}

/**
 * Recursively traverses a Moddle tree (e.g., BPMN/DMN elements)
 * using a depth-first approach, executing a callback for each element. Skips:
 * - Generic descriptor elements
 * - XML attributes
 * - Reference properties
 * - String-type properties
 *
 * @param {Object} element - Root Moddle element to start traversal from
 * @param {Function} callback - Function invoked for each element. Return `false` to stop further traversal of the current branch
 *
 * @example
 * // Log all element IDs in the tree
 * traverseModdle(rootElement, (el) => {
 *   console.log(el.get('id'));
 * });
 *
 * @example
 * // Find specific element and stop traversal
 * let target;
 * traverseModdle(rootElement, (el) => {
 *   if (el.get('id') === 'desired-id') {
 *     target = el;
 *     return false; // Stop searching
 *   }
 * });
 */
export function traverseModdle(element, callback) {
  if (callback(element) === false) {
    return;
  }

  const descriptor = element.$descriptor;

  if (!descriptor.isGeneric) {
    const properties = descriptor.properties.filter((property) => {
      return !property.isAttr && !property.isReference && property.type !== 'String';
    });

    properties.forEach((property) => {
      if (property.name in element) {
        const propertyValue = element[property.name];

        if (property.isMany) {
          propertyValue.forEach((p) => {
            traverseModdle(p, callback);
          });
        } else {
          traverseModdle(propertyValue, callback);
        }
      }
    });
  }
}
